import { HttpResponse } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CorporateEntity, Report } from 'src/app/model';
import { BVQReport, Competitor, Customer, Location, Owner, Product, SourceValue } from 'src/app/model/bvq';
import { Contact } from 'src/app/model/bvq/contact';
import { AskPageError, DocumentType } from 'src/app/model/bvq/enums';
import { SearchableItem } from 'src/app/model/interface';
import { Sic } from 'src/app/model/sic';
import { BVQReportProvider } from 'src/app/providers/bvq';
import { ReportService } from 'src/app/services/report.service';
import { ContentResponse } from '../../research/model';
import { IndustryBasedQuestion } from '../components/ask-builder/models/IndustryBased';
import { ReportSourceService } from './source.service';
import { LogService } from 'src/app/services';

@Injectable()
export class BVQReportService {
  report = new BehaviorSubject<BVQReport>(null);
  report$ = this.report.asObservable();

  private selectedSource: SourceValue;
  private sourceSub: any;

  sics = new BehaviorSubject<SearchableItem[]>([]);
  sics$ = this.sics.asObservable();

  private visited = {};

  changes = new BehaviorSubject<boolean>(false);
  changes$ = this.changes.asObservable();

  showAsk = new BehaviorSubject<boolean>(true);
  showAsk$ = this.showAsk.asObservable();

  expand = new BehaviorSubject<boolean>(true);
  expand$ = this.expand.asObservable();

  private isResearch = new BehaviorSubject<boolean>(true);
  isResearch$ = this.isResearch.asObservable();

  private mainReport: Report;
  private reportSub: any;

  industry = new BehaviorSubject<IndustryBasedQuestion>(new IndustryBasedQuestion());
  industry$ = this.report.asObservable();

  onDetailPage: EventEmitter<any> = new EventEmitter();

  constructor(
    private sourceService: ReportSourceService,
    private reportProvider: BVQReportProvider,
    private reportService: ReportService,
    private logService: LogService
  ) {
    this.sourceSub = this.sourceService.source$.subscribe(selectedSource => this.selectedSource = selectedSource);
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      if (currentReport) {
        this.mainReport = currentReport;
      }
    });
  }

  setChanges(changes: boolean): void {
    this.changes.next(changes);
  }

  setExpand(expand: boolean): void {
    this.expand.next(expand);
  }

  setResearch(isResearch: boolean): void {
    this.isResearch.next(isResearch);
  }

  setReport(report: BVQReport): void {
    if (!report.ASK) report.ASK = [];
    if (report.NAICS) {
      if (report.NAICS.NAICS_SICS) {
        this.setSic(Sic.parseArray(report.NAICS.NAICS_SICS));
      }
    }

    this.report.next(report);
  }

  clearReport(): void {
    this.report.next(null);
  }

  getReport(): BVQReport {
    return this.report.getValue();
  }

  setSic(sic: SearchableItem[]): void {
    this.sics.next(sic);
  }

  updateNaics(id: string, value: any): void {
    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES['NAICS'] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    this.report.getValue().ASK['NAICS'] = {
      VALUE: value || value === false ? false : true
    };

    this.reportProvider.updateNaics(id, value,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE)
      .subscribe(data => {
        if (data.success) {
          let sics: SearchableItem[] = [];
          if (data.result)
            if (data.result.NAICS_SICS)
              sics = Sic.parseArray(data.result.NAICS_SICS);

          this.setSic(sics);
        }
      }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  updateField(field: string, value: any): void {
    // Create new Source if doesn't exist
    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    // Change Source only if there was no Source before
    if (!this.report.getValue().SOURCES[field]) {
      if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
        this.report.getValue().SOURCES[field] = {
          TYPE: this.selectedSource.TYPE,
          NOTE: this.selectedSource.NOTE,
          SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
        };
      }
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveReportField(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  fieldSourceChanged(field: string, sourceType: string, sourceNote: string, sourceTitle: string): void {
    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    this.report.getValue().SOURCES[field] = {
      TYPE: sourceType,
      NOTE: sourceNote,
      SOURCE_TITLE: sourceTitle
    };
  }

  validateGroup(group: string): boolean {
    switch (group) {
      case 'ownership':
        if (this.visited[group])
          return this.validateGroupOwnership();
        else return true;
      case 'psbreakdown':
        if (this.visited[group])
          return this.validateGroupProducts();
        else return true;
      default:
        return false;
    }
  }

  setGroupVisited(group: string): void {
    setTimeout(() => {
      this.visited[group] = true;
    }, 50);
  }

  private validateGroupOwnership(): boolean {
    let totalPercent = 0;

    if (this.report.getValue().OWNERS) {
      for (let i = 0; i < this.report.getValue().OWNERS.length; i++) {
        if (this.report.getValue().OWNERS[i].PERCENTAGE)
          totalPercent = totalPercent + this.report.getValue().OWNERS[i].PERCENTAGE;

        if (!this.report.getValue().OWNERS[i].NAME || this.report.getValue().OWNERS[i].NAME.length === 0)
          return false;
      }
      return totalPercent === 100;
    } else return false;
  }

  private validateGroupProducts(): boolean {
    let totalPercent = 0;

    if (this.report.getValue().PRODUCTS) {
      for (let i = 0; i < this.report.getValue().PRODUCTS.length; i++) {
        if (this.report.getValue().PRODUCTS[i].REVENUE)
          totalPercent = totalPercent + this.report.getValue().PRODUCTS[i].REVENUE;

        if (!this.report.getValue().PRODUCTS[i].NAME || this.report.getValue().PRODUCTS[i].NAME.length === 0)
          return false;
      }
      return totalPercent === 100;
    } else return false;
  }


  getDocumentType(fileName: string): DocumentType {
    let documentType = null;

    if (fileName.toLowerCase().endsWith('.pdf')) {
      documentType = DocumentType.PDF;
    } else if (fileName.toLowerCase().endsWith('.doc') || fileName.toLowerCase().endsWith('.docx')) {
      documentType = DocumentType.DOCX;
    } else if (fileName.toLowerCase().endsWith('.jpeg') || fileName.toLowerCase().endsWith('.jpg') || fileName.toLowerCase().endsWith('.png')) {
      documentType = DocumentType.JPEG;
    } else if (fileName.toLowerCase().endsWith('.txt')) {
      documentType = DocumentType.TEXT;
    } else if (fileName.toLowerCase().endsWith('.rar')) {
      documentType = DocumentType.RAR;
    } else if (fileName.toLowerCase().endsWith('.zip')) {
      documentType = DocumentType.ZIP;
    } else if (fileName.toLowerCase().endsWith('.xls') || fileName.toLowerCase().endsWith('.xlsx')) {
      documentType = DocumentType.XLSX;
    }

    return documentType;
  }

  //Entities
  corporateEntityFieldChanged(entity: CorporateEntity, column: string, value: any): void {
    let field: string = `ENTITIES-${column}:${entity.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveCorporateEntityFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, entity.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  // Owners
  ownerFieldChanged(eid: string, owner: Owner, column: string, value: any): void {
    let field: string = `ENTITIES-OWNERS-${column}:${eid}:${owner.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveOwnerFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, owner.ID, column, value, eid, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  // Locations
  locationFieldChanged(location: Location, column: string, value: any): void {
    let field: string = `LOCATIONS-${column}:${location.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveLocationFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, location.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  // Competitor
  competitorFieldChanged(competitor: Competitor, column: string, value: any): void {
    let field: string = `COMPETITORS-${column}:${competitor.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveCompeitorFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, competitor.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  //Product
  productFieldChanged(product: Product, column: string, value: any): void {
    let field: string = `PRODUCTS-${column}:${product.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveProductFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, product.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  //Client Customer
  customerFieldChanged(customer: Customer, column: string, value: any): void {
    let field: string = `CLIENT_CUSTOMERS-${column}:${customer.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveCustomerFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, customer.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  //Contact
  contactFieldChanged(contact: Contact, column: string, value: any): void {
    let field: string = `CONTACTS-${column}:${contact.ID}`;

    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
      this.report.getValue().SOURCES[field] = {
        TYPE: this.selectedSource.TYPE,
        NOTE: this.selectedSource.NOTE,
        SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
      };
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[field]) {
          delete this.report.getValue().SOURCES[field];
        }
      }
    }

    this.report.getValue().ASK[field] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveContactFieldSource(this.report.getValue(), field,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''),
      this.selectedSource.NOTE, contact.ID, column, value, this.selectedSource.SOURCE_TITLE)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }

  //census
  censusChanged(location: Location, value: any): void {
    this.reportProvider.saveCensus(this.report.getValue(), location.ID, value)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Census Field!');
        }, err => this.logService.log('Error Saving Census Field [' + err + ']'));
  }

  initializeIndustryField(...fields: string[]): void {
    if (!this.report.getValue().INDUSTRY)
      this.report.getValue().INDUSTRY = {};

    for (const field of fields) {
      if (!this.report.getValue().INDUSTRY[field])
        this.report.getValue().INDUSTRY[field] = {};
    }
  }

  setReportAsk(value: boolean): void {
    this.showAsk.next(value);
  }

  updateAskField(field: string, value: any): void {
    if (!this.report.getValue().ASK[field]) {
      this.report.getValue().ASK[field] = {
        VALUE: value,
        RESEND: false
      };
    } else {
      this.report.getValue().ASK[field].VALUE = value;
    }
  }

  updateResendField(field: string, value: any): void {
    if (!this.report.getValue().ASK[field]) {
      this.report.getValue().ASK[field] = {
        VALUE: false,
        RESEND: value
      };
    } else {
      this.report.getValue().ASK[field].RESEND = value;
    }
  }

  hasWarning(highlight: boolean = false, page: AskPageError): boolean {
    if (highlight && this.mainReport) {
      let error: boolean = false;
      switch (page) {
        // case AskPageError.COMPETITION:
        //   this.report.getValue().COMPETITORS.map((competitor) => {
        //     if (this.report.getValue().hasAsk('COMPETITORS-NAME:' + competitor.ID) ||
        //       this.report.getValue().hasAsk('COMPETITORS-ADDRESS:' + competitor.ID) ||
        //       this.report.getValue().hasAsk('COMPETITORS-WEBSITE:' + competitor.ID) ||
        //       this.report.getValue().hasAsk('COMPETITORS-DESCRIPTION:' + competitor.ID) ||
        //       this.report.getValue().hasAsk('COMPETITORS-COMP_ANALYSIS:' + competitor.ID))
        //       error = true;
        //   });
        //   break;
        // case AskPageError.FINANCIALS:
        //   if (this.report.getValue().hasAsk('SALES_INC_DEC') ||
        //     this.report.getValue().hasAsk('SEASONALITY') ||
        //     this.report.getValue().hasAsk('PROJECTED_REVENUE_CURRENT') ||
        //     this.report.getValue().hasAsk('PROJECTED_REVENUE_NEXT'))
        //     error = true;
        //   break;
        case AskPageError.GENERAL:
          if (this.mainReport.ENTITIES && this.mainReport.ENTITIES.length > 0) {
            this.mainReport.ENTITIES.map((entity) => {
              if (this.report.getValue().hasAsk('ENTITIES-NAME:' + entity.ID) ||
                this.report.getValue().hasAsk('ENTITIES-DBA:' + entity.ID))
                error = true;
            });
          }
          break;
        // case AskPageError.INVENTORY:
        //   if (this.report.getValue().hasAsk('PURCHASE_NEW_INV_REQUIRED') ||
        //     this.report.getValue().hasAsk('EQUIPMENT_AVG_AGE'))
        //     error = true;
        //   break;
        case AskPageError.LOCATION:
          if (this.report.getValue().hasAsk('COMPANY_MORE_LOCATIONS'))
            error = true;

          if (error == false) {
            this.report.getValue().LOCATIONS.map((location) => {
              if (this.report.getValue().hasAsk('LOCATIONS-ADDRESS:' + location.ID) ||
                this.report.getValue().hasAsk('LOCATIONS-ZIP:' + location.ID) ||
                this.report.getValue().hasAsk('LOCATIONS-STATE:' + location.ID) ||
                this.report.getValue().hasAsk('LOCATIONS-CITY:' + location.ID) ||
                this.report.getValue().hasAsk('LOCATIONS-OWNERSHIP_TYPE:' + location.ID) ||
                (location.OWNERSHIP_TYPE === 'OWNED' && this.report.getValue().hasAsk('LOCATIONS-OWNED_BY:' + location.ID)) ||
                this.report.getValue().hasAsk('LOCATIONS-TOTAL_SQFT:' + location.ID) ||
                this.report.getValue().hasAsk('LOCATIONS-BUILDING_TYPE:' + location.ID))
                error = true;
            });
          }
          break;
        case AskPageError.MANAGEMENT:
          if (this.report.getValue().hasAsk('NUMBER_OF_EMPLOYEES') ||
            this.report.getValue().hasAsk('EMPLOYEES_RELATED_TO_SELLER') ||
            (this.report.getValue().EMPLOYEES_RELATED_TO_SELLER && this.report.getValue().hasAsk('EMPLOYEES_RELATED_TO_SELLER_WAGE')) ||
            (this.report.getValue().EMPLOYEES_RELATED_TO_SELLER && this.report.getValue().EMPLOYEES_RELATED_TO_SELLER_WAGE === false &&
              (this.report.getValue().hasAsk('EMPLOYEES_RELATED_HOURS_WORKED') ||
                this.report.getValue().hasAsk('EMPLOYEES_RELATED_SALARY') ||
                this.report.getValue().hasAsk('EMPLOYEES_RELATED_WAGE_STATEMENT'))))
            error = true;
          break;
        case AskPageError.OVERVIEW:
          if (this.report.getValue().hasAsk('YEARS_IN_BUSINESS') ||
            this.report.getValue().hasAsk('HAS_SUBSIDIARY_COMPANIES') ||
            (this.report.getValue().HAS_SUBSIDIARY_COMPANIES && this.report.getValue().hasAsk('SUBSIDIARY_COMPANIES')) ||
            (this.report.getValue().HAS_SUBSIDIARY_COMPANIES && this.report.getValue().hasAsk('SUBSIDIARY_COMPANIES_BUSINESS')) ||
            this.report.getValue().hasAsk('IS_SUBSIDIARY_COMPANY') ||
            (this.report.getValue().IS_SUBSIDIARY_COMPANY && this.report.getValue().hasAsk('PARENT_COMPANY')) ||
            (this.report.getValue().IS_SUBSIDIARY_COMPANY && this.report.getValue().hasAsk('IS_SUBSIDIARY_DESCRIBE')) ||
            this.report.getValue().hasAsk('RESTRICTIONS_PENDING') ||
            (this.report.getValue().RESTRICTIONS_PENDING && this.report.getValue().hasAsk('RESTRICTIONS_DESCRIPTION')) ||
            this.report.getValue().hasAsk('COMPANY_DOES'))
            error = true;
          break;
        case AskPageError.OWNERSHIP:
          if (this.mainReport.ENTITIES && this.mainReport.ENTITIES.length > 0) {
            this.mainReport.ENTITIES.map((entity) => {
              if (entity.OWNERS.length > 0) {
                entity.OWNERS.map((owner) => {
                  if (this.report.getValue().hasAsk('ENTITIES-OWNERS-NAME:' + entity.ID + ':' + owner.ID) ||
                    this.report.getValue().hasAsk('ENTITIES-OWNERS-PERCENTAGE:' + entity.ID + ':' + owner.ID) ||
                    this.report.getValue().hasAsk('ENTITIES-OWNERS-HOURS_WORKED:' + entity.ID + ':' + owner.ID) ||
                    this.report.getValue().hasAsk('ENTITIES-OWNERS-RESPONSIBILITIES:' + entity.ID + ':' + owner.ID))
                    error = true;
                });
              }
            });
          }
          break;
        case AskPageError.RESEARCH:
          if (!this.report.getValue().NAICS || !this.report.getValue().SIC ||
            !this.report.getValue().IBIS || !this.report.getValue().IBIS_DATE ||
            !this.report.getValue().RESEARCH_IBIS_LAST || !this.report.getValue().RESEARCH_IBIS_NEXT ||
            !this.report.getValue().ROT
          )
            error = true;
          break;
        // case AskPageError.PS:
        //   this.report.getValue().PRODUCTS.map((product) => {
        //     if (this.report.getValue().hasAsk('PRODUCTS-NAME:' + product.ID) ||
        //       this.report.getValue().hasAsk('PRODUCTS-REVENUE:' + product.ID))
        //       error = true;
        //   });
        //   break;
        case AskPageError.MEDICAL:
          if (this.report.getValue().hasAsk('MEDPR-PAYER_BREAKDOWN') ||
            this.report.getValue().hasAsk('MEDPR-ACTIVE_PATIENTS') ||
            this.report.getValue().hasAsk('MEDPR-AVG_LENGTH'))
            error = true;
          break;
        case AskPageError.GAS:
          if (this.report.getValue().hasAsk('GASST-TOTAL_GALONS') ||
            this.report.getValue().hasAsk('GASST-POOL_MARGIN'))
            error = true;
          break;
        case AskPageError.DAYCARE:
          if (this.report.getValue().hasAsk('DAYCR-FACILITY_CAPACITY') ||
            this.report.getValue().hasAsk('DAYCR-AVG_FTE') ||
            this.report.getValue().hasAsk('DAYCR-AVG_FTE_RATE'))
            error = true;
          break;
        case AskPageError.SOFTWARE:
          if (this.report.getValue().hasAsk('SFTIT-REVENUE_MODEL'))
            error = true;
          break;
        case AskPageError.ACCOUNTING:
          if (this.report.getValue().hasAsk('ACCTP-CLIENTS_MIX') ||
            this.report.getValue().hasAsk('ACCTP-AVG_PRICING'))
            error = true;
          break;
        case AskPageError.PHARMACY:
          if (this.report.getValue().hasAsk('PHARM-PHARMACISTS_TOTAL') ||
            this.report.getValue().hasAsk('PHARM-PRESC_AMOUNT') ||
            this.report.getValue().hasAsk('PHARM-PRIMARY_WHOLESALER') ||
            this.report.getValue().hasAsk('PHARM-ASSOC_PURCH_GROUPS') ||
            (this.report.getValue().INDUSTRY['PHARM-ASSOC_PURCH_GROUPS'].VALUE === 'Y' && this.report.getValue().hasAsk('PHARM-PURCHASING_GROUPS')) ||
            this.report.getValue().hasAsk('PHARM-PAYER_BREAKDOWN') ||
            this.report.getValue().hasAsk('PHARM-REVENUE_BREAKDOWN'))
            error = true;
          break;
        case AskPageError.MEMBERSHIP:
          if (this.report.getValue().hasAsk('MBRSP-TOTAL_MEMBERS') ||
            this.report.getValue().hasAsk('MBRSP-PRICING_MODEL'))
            error = true;
          break;
        case AskPageError.FEDEX:
          if (this.report.getValue().hasAsk('FEDEX-ROUTES') ||
            this.report.getValue().hasAsk('FEDEX-ZIPS') ||
            this.report.getValue().hasAsk('FEDEX-TRUCKS'))
            error = true;
            // this.report.getValue().hasAsk('FEDEX-DURATION') ||
            // this.report.getValue().hasAsk('FEDEX-RENEW'))
            
          break;
        case AskPageError.RIA:
          if (this.report.getValue().hasAsk('REGAD-TOTAL_CLIENTS') ||
            this.report.getValue().hasAsk('REGAD-PERC_CLIENTS') ||
            this.report.getValue().hasAsk('REGAD-TOTAL_VALUE') ||
            this.report.getValue().hasAsk('REGAD-FEE_SCHEDULE') ||
            this.report.getValue().hasAsk('REGAD-RECURRING_CLIENTS') ||
            this.report.getValue().hasAsk('REGAD-ASSSETS_MANAGEMENT'))
            error = true;
          break;
        case AskPageError.VETERINARY:
          if (this.report.getValue().hasAsk('VTRNY-PAYER_BREAKDOWN') ||
            this.report.getValue().hasAsk('VTRNY-ACTIVE_PATIENTS'))
            error = true;
          break;
        case AskPageError.AUTOBODY:
          if (this.report.getValue().hasAsk('AUTOB-INSURANCE_CARRIER') ||
            this.report.getValue().hasAsk('AUTOB-DIRECT_REPAIR') ||
            (this.report.getValue().INDUSTRY['AUTOB-DIRECT_REPAIR'].VALUE == 'Y' && this.report.getValue().hasAsk('AUTOB-DIRECT_REPAIR_DESC')))
            error = true;
          break;
        case AskPageError.GIFT:
          if (this.report.getValue().hasAsk('GIFTC-HANDLING') ||
            this.report.getValue().hasAsk('GIFTC-TRANSFER'))
            error = true;
          break;
        case AskPageError.AIB:
          if (this.report.getValue().hasAsk('AIB01-EQUIPMENT_AGE') ||
            this.report.getValue().hasAsk('AIB01-NUMER_WD'))
            error = true;
          break;
        case AskPageError.WHOLESALE:
          if (this.report.getValue().hasAsk('MNFWS-CLIENT_AMT') ||
            this.report.getValue().hasAsk('MNFWS-CLIENT_TOP_REV') ||
            this.report.getValue().hasAsk('MNFWS-REVBR_PROD') ||
            this.report.getValue().hasAsk('MNFWS-REV_SINGLE') ||
            this.report.getValue().hasAsk('MNFWS-REV_LIST'))
            error = true;
          break;
        default:
          break;
      }
      return error;
    } else
      return false;
  }

  defineGeneralManager(): boolean {
    if (this.report.getValue().GENERAL_MANAGER_DISPLAY)
      return true;
    return this.isManagerShowing();
  }

  isManagerShowing(): boolean {
    let maxHours = 0;

    if (this.report.getValue())
      if (this.report.getValue().OWNERS)
        for (let i = 0; i < this.report.getValue().OWNERS.length; i++)
          if (this.report.getValue().OWNERS[i].HOURS_WORKED > maxHours)
            maxHours = this.report.getValue().OWNERS[i].HOURS_WORKED;

    return maxHours < 20;
  }

  getDateYearSub(years: number): Date {
    const date = new Date(this.report.getValue().dateCreated);
    date.setFullYear(date.getFullYear() - years);
    return date;
  }

  resetField(sourceField: string): void {
    this.report.getValue().INDUSTRY[sourceField].VALUE = null;
    this.fieldChangedSeparate(sourceField, sourceField);
  }

  fieldChanged(field: string): void {
    this.fieldChangedSeparate(field, field);
  }

  fieldChangedSeparate(askField: string, sourceField: string): void {
    if (!this.report.getValue().SOURCES)
      this.report.getValue().SOURCES = [];

    let value = this.report.getValue().INDUSTRY[sourceField].VALUE;

    // Change Source only if there was no Source before
    if (!this.report.getValue().SOURCES[sourceField]) {
      if (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null') {
        this.report.getValue().SOURCES[sourceField] = {
          TYPE: this.selectedSource.TYPE,
          NOTE: this.selectedSource.NOTE,
          SOURCE_TITLE: this.selectedSource.SOURCE_TITLE
        };
      }
    }

    if (typeof value == 'string') {
      if (!value) {
        if (this.report.getValue().SOURCES[sourceField]) {
          delete this.report.getValue().SOURCES[sourceField];
        }
      }
    }
    else {
      if (value == null) {
        if (this.report.getValue().SOURCES[sourceField]) {
          delete this.report.getValue().SOURCES[sourceField];
        }
      }
    }

    this.report.getValue().ASK[askField] = {
      VALUE: !(value || value === false)
    };

    this.reportProvider.saveReportIndustrySource(this.report.getValue(), sourceField, askField,
      (this.selectedSource.TYPE != null && this.selectedSource.TYPE != 'null' ? this.selectedSource.TYPE : ''), this.selectedSource.NOTE, value)
      .subscribe(
        data => {
          if (!data.success)
            this.logService.log('Error Saving Source for Field!');
        }, err => this.logService.log('Error Saving Source for Field [' + err + ']'));
  }


  downloadFile(path: string): void {
    this.reportProvider.downloadFile(path)
      .subscribe(response => {
        let filename: string = this.getFileName(response);

        const blob = new Blob([response.body], {});
        saveAs(blob, filename);
      }, err => {
        this.logService.error(err);
      });
  }

  getFileName(response: HttpResponse<Blob>): string {
    try {
      let contentDispositionHeader = response.headers.get('Content-Disposition');
      let result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      return result.replace(/"/g, '');
    } catch (e) {
      return 'file.pdf';
    }
  }

}
